// Read-only progress. Final settlement and winner/tie-break writes stay in SQL.
const score = value => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const liveStates = new Set(['live','in_progress','in_play','1H','HT','2H','ET','BT','P','INT']);
export const pontoonTerminal = fixture => ['complete','postponed','abandoned'].includes(fixture.status);

export function pontoonContribution(pick, fixtures, now = Date.now()) {
  const base = {team:pick.selected_team,fixtureId:pick.fixture_id,points:null,goals:null,state:'unavailable',label:'Score unavailable'};
  const fixture = fixtures?.find(f => f.id === pick.fixture_id);
  if (!fixture || ![fixture.home_team,fixture.away_team].includes(pick.selected_team)
    || pick.match_day!=null&&fixture.match_day!=null&&pick.match_day!==fixture.match_day) return base;
  const home = pick.selected_team === fixture.home_team;
  const result = {...base,opponent:home?fixture.away_team:fixture.home_team,kickoff:fixture.kickoff_time};
  if (['postponed','abandoned'].includes(fixture.status)) return {...result,state:'void',label:fixture.status==='postponed'?'Postponed · void pending':'Abandoned · void pending'};
  if (fixture.status==='cancelled') return {...result,label:'Cancelled · rule unresolved'};
  const goals = home ? fixture.home_score : fixture.away_score;
  if (fixture.status==='complete') return score(goals)
    ? {...result,state:'finished',goals,points:goals===0?10:goals,label:'Finished'} : result;
  if (liveStates.has(fixture.status)) return score(goals)
    ? {...result,state:'live',goals,points:goals,label:'Live · provisional'} : {...result,label:'Live · score unavailable'};
  const kickoff = Date.parse(fixture.kickoff_time);
  if (['incomplete','scheduled','not_started','NS'].includes(fixture.status) && Number.isFinite(kickoff) && kickoff > now)
    return {...result,state:'not_started',label:'Not started'};
  // A generic incomplete status is not proof of live play. A reported numeric
  // score may be shown provisionally, but never converted from zero to ten.
  if (fixture.status==='incomplete' && score(goals)) return {...result,state:'reported',points:goals,goals,label:'Reported score · phase unavailable'};
  return result;
}

export function pontoonMatchdayGoals(fixtures, now=Date.now()) {
  if (!fixtures?.length) return {total:null,final:false,label:'Matchday goals unavailable'};
  let total=0,known=0;
  for (const f of fixtures) if ((f.status==='complete'||liveStates.has(f.status)||f.status==='incomplete') && score(f.home_score)&&score(f.away_score)) {
    // Ignore any prefilled not-started scores; only confirmed live/final scores
    // or ambiguous reported data belong in the provisional goals total.
    if(f.status==='incomplete' && Date.parse(f.kickoff_time)>now)continue;
    total+=f.home_score+f.away_score;known++;
  }
  const final=fixtures.every(f=>pontoonTerminal(f) && (f.status!=='complete'||score(f.home_score)&&score(f.away_score)));
  return {total:known||final?total:null,final,label:final?'Actual matchday goals':'Matchday goals so far · provisional'};
}

export function projectPontoonProgress({picks,fixtures,record,now=Date.now()}) {
  const contributions=(picks||[]).map(p=>pontoonContribution(p,fixtures,now));
  const finished=contributions.filter(c=>c.state==='finished');
  const settled=finished.reduce((sum,c)=>sum+c.points,0);
  const available=contributions.filter(c=>c.points!=null);
  const total=available.length?available.reduce((sum,c)=>sum+c.points,0):null;
  const hasVoid=contributions.some(c=>c.state==='void');
  const allFinished=contributions.length>0&&finished.length===contributions.length;
  const expected=hasVoid?'void':allFinished?(settled===21?'pontoon':settled>21?'bust':'under'):null;
  const mismatch=record?.outcome!=null && (expected!=null&&(expected!==record.outcome||(expected!=='void'&&record.weekly_score!==settled))
    || record.outcome!=='void'&&record.weekly_score!=null&&settled>record.weekly_score
    || !allFinished&&!hasVoid&&contributions.some(c=>['live','not_started','reported'].includes(c.state)));
  let outcome='Pending fixtures',tone='neutral';
  if(hasVoid){outcome=record?.outcome==='void'?'Void':'Void pending settlement';tone='warning';}
  else if(settled>21){outcome='Bust · settled contributions exceed 21';tone='danger';}
  else if(allFinished){outcome=settled===21?'21 · awaiting final settlement':settled>21?'Bust':'Under 21 · awaiting settlement';}
  else if(total!=null){outcome=total===21?'Provisional 21':total>21?'Over 21 · provisional':'Matchday in progress';}
  const recorded=record?.outcome!=null&&!mismatch;
  if(recorded){outcome={pontoon:'Pontoon (21)',bust:'Bust',under:'Under 21',void:'Void'}[record.outcome]||'Recorded result';tone=record.outcome==='pontoon'?'success':record.outcome==='bust'?'danger':record.outcome==='void'?'warning':'neutral';}
  if(mismatch){outcome='Score correction · settlement needs review';tone='warning';}
  return {contributions,settled,total,finished:finished.length,pickCount:contributions.length,hasVoid,allFinished,
    recorded,mismatch,outcome,tone,displayScore:recorded?record.weekly_score:total,
    scoreLabel:recorded?'Recorded Pontoon score':allFinished?'Calculated Pontoon score':'Provisional Pontoon score',
    winner:recorded&&record.outcome==='pontoon'&&record.is_winner===true&&allFinished&&pontoonMatchdayGoals(fixtures,now).final,
    recordedScore:record?.weekly_score??null,recordedOutcome:record?.outcome??null};
}
